import React, { useState, useEffect } from 'react';
import api from '../services/apiService';
import { authService } from '../services/authService';
import { Link } from 'react-router-dom';

const PushNotificationAdmin = () => {
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [targetType, setTargetType] = useState('all');
  const [teamId, setTeamId] = useState('');
  const [matchId, setMatchId] = useState('');
  const [selectedPlayers, setSelectedPlayers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [matches, setMatches] = useState([]);
  const [players, setPlayers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  // Fetch teams, matches, and players on component mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch teams
        const teamsResponse = await api.get('get_teams.php');
        if (teamsResponse.data.success) {
          setTeams(teamsResponse.data.teams || []);
        }

        // Fetch upcoming matches
        const matchesResponse = await api.get('get_matches.php?upcoming=true');
        if (matchesResponse.data.success) {
          setMatches(matchesResponse.data.matches || []);
        }

        // Fetch players
        const playersResponse = await api.get('get_players.php');
        if (playersResponse.data.success) {
          setPlayers(playersResponse.data.players || []);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
        setError('Failed to load data. Please try again.');
      }
    };

    fetchData();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setResult(null);
    setError('');

    try {
      // Validate form
      if (!title.trim()) {
        throw new Error('Notification title is required');
      }
      
      if (!body.trim()) {
        throw new Error('Notification message is required');
      }
      
      if (targetType === 'team' && !teamId) {
        throw new Error('Please select a team');
      }
      
      if (targetType === 'match' && !matchId) {
        throw new Error('Please select a match');
      }
      
      if (targetType === 'players' && selectedPlayers.length === 0) {
        throw new Error('Please select at least one player');
      }

      // Prepare request data
      const requestData = {
        title,
        body,
        data: {} // Additional data to send with the notification
      };

      // Add target-specific data
      if (targetType === 'team' && teamId) {
        requestData.team_id = teamId;
      } else if (targetType === 'match' && matchId) {
        requestData.match_id = matchId;
      } else if (targetType === 'players' && selectedPlayers.length > 0) {
        requestData.player_ids = selectedPlayers;
      }

      // Send the notification
      const response = await api.post('send_push_notification.php', requestData);
      
      if (response.data.success) {
        setResult(response.data);
        // Reset form on success
        setTitle('');
        setBody('');
        setTargetType('all');
        setTeamId('');
        setMatchId('');
        setSelectedPlayers([]);
      } else {
        // Build detailed error message with debug info
        let errorMessage = response.data.message || 'Failed to send notification';
        
        if (response.data.errors && response.data.errors.length > 0) {
          errorMessage += '\n\nErrors:\n' + response.data.errors.join('\n');
        }
        
        if (response.data.debug) {
          errorMessage += '\n\nDebug Information:';
          if (typeof response.data.debug === 'object') {
            Object.keys(response.data.debug).forEach(key => {
              if (Array.isArray(response.data.debug[key])) {
                errorMessage += `\n${key}:\n  - ` + response.data.debug[key].join('\n  - ');
              } else {
                errorMessage += `\n${key}: ${JSON.stringify(response.data.debug[key])}`;
              }
            });
          } else {
            errorMessage += '\n' + JSON.stringify(response.data.debug, null, 2);
          }
        }
        
        if (response.data.fcm_response_android || response.data.fcm_response_ios) {
          errorMessage += '\n\nFCM Response:';
          if (response.data.fcm_response_android) {
            errorMessage += '\nAndroid: ' + JSON.stringify(response.data.fcm_response_android, null, 2);
          }
          if (response.data.fcm_response_ios) {
            errorMessage += '\niOS: ' + JSON.stringify(response.data.fcm_response_ios, null, 2);
          }
        }
        
        setError(errorMessage);
        setResult(response.data); // Also set result so debug info can be displayed
      }
    } catch (error) {
      console.error('Error sending notification:', error);
      let errorMessage = 'An error occurred while sending the notification';
      
      if (error.response && error.response.data) {
        errorMessage = error.response.data.message || 'Server error occurred';
        
        // Add debug info if available
        if (error.response.data.debug) {
          errorMessage += '\n\nDebug Information:\n' + JSON.stringify(error.response.data.debug, null, 2);
        }
      } else if (error.message) {
        errorMessage = error.message;
        if (error.stack) {
          errorMessage += '\n\nStack trace:\n' + error.stack;
        }
      }
      
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  // Check if user is admin
  if (!authService.isAdmin()) {
    return (
      <div className="push-notification-admin">
        <h2>Unauthorized</h2>
        <p>You do not have permission to access this page.</p>
      </div>
    );
  }

  return (
    <div className="push-notification-admin">
      <h2>Send Push Notifications</h2>
      
      <div className="dev-tools">
        <Link to="/push-notifications/test" className="tester-link">
          Open Push Notification Tester
        </Link>
      </div>
      
      {error && (
        <div className="error-message" style={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: '12px', maxHeight: '400px', overflow: 'auto', padding: '10px', backgroundColor: '#f8f8f8', border: '1px solid #ddd', borderRadius: '4px' }}>
          <strong>Error Details:</strong>
          <pre style={{ margin: '10px 0', whiteSpace: 'pre-wrap' }}>{error}</pre>
        </div>
      )}
      
      {result && (
        <div className={result.success ? "success-message" : "error-message"}>
          <h3>{result.success ? 'Notification Sent!' : 'Notification Failed'}</h3>
          <p>Total recipients: {result.stats?.total || 0}</p>
          <p>Successful deliveries: {result.stats?.success || 0}</p>
          <p>Failed deliveries: {result.stats?.failure || 0}</p>
          {result.stats && (
            <>
              <p>Android tokens: {result.stats.android || 0}</p>
              <p>iOS tokens: {result.stats.ios || 0}</p>
            </>
          )}
          
          {result.debug && (
            <div style={{ marginTop: '15px', padding: '10px', backgroundColor: '#f0f0f0', borderRadius: '4px' }}>
              <strong>Debug Information:</strong>
              <pre style={{ margin: '10px 0', whiteSpace: 'pre-wrap', fontSize: '11px', maxHeight: '400px', overflow: 'auto', fontFamily: 'monospace' }}>
                {(() => {
                  // Handle nested debug structure
                  let debugContent = result.debug;
                  
                  // If debug has a 'combined' key, extract it
                  if (debugContent && typeof debugContent === 'object' && debugContent.combined) {
                    debugContent = debugContent.combined;
                  }
                  
                  // If it's an array, join it
                  if (Array.isArray(debugContent)) {
                    return debugContent.join('\n');
                  }
                  
                  // If it's an object, stringify it
                  if (typeof debugContent === 'object') {
                    return JSON.stringify(debugContent, null, 2);
                  }
                  
                  return String(debugContent);
                })()}
              </pre>
              
              {/* Extract and highlight iOS APNs payload if present */}
              {(() => {
                let debugContent = result.debug;
                if (debugContent && typeof debugContent === 'object' && debugContent.combined) {
                  debugContent = debugContent.combined;
                }
                
                if (Array.isArray(debugContent)) {
                  const apnsPayload = debugContent.find(line => line.includes('FCM Payload (iOS APNs section)'));
                  if (apnsPayload) {
                    return (
                      <div style={{ marginTop: '15px', padding: '10px', backgroundColor: '#e6f3ff', borderRadius: '4px', border: '2px solid #4a90e2' }}>
                        <strong style={{ color: '#4a90e2' }}>📱 iOS APNs Payload (Sound Configuration):</strong>
                        <pre style={{ margin: '10px 0', whiteSpace: 'pre-wrap', fontSize: '12px', fontFamily: 'monospace', backgroundColor: '#fff', padding: '10px', borderRadius: '4px' }}>
                          {apnsPayload.replace('FCM Payload (iOS APNs section): ', '')}
                        </pre>
                        <p style={{ fontSize: '11px', color: '#666', marginTop: '5px' }}>
                          Look for <strong>"sound": "whistle"</strong> in the payload above
                        </p>
                      </div>
                    );
                  }
                }
                return null;
              })()}
            </div>
          )}
          
          {result.errors && (
            <div style={{ marginTop: '15px', padding: '10px', backgroundColor: '#ffe6e6', borderRadius: '4px' }}>
              <strong>Errors:</strong>
              <ul>
                {result.errors.map((err, idx) => (
                  <li key={idx}>{err}</li>
                ))}
              </ul>
            </div>
          )}
          
          {(result.fcm_response_android || result.fcm_response_ios) && (
            <div style={{ marginTop: '15px', padding: '10px', backgroundColor: '#e6f3ff', borderRadius: '4px' }}>
              <strong>FCM Response:</strong>
              <pre style={{ margin: '10px 0', whiteSpace: 'pre-wrap', fontSize: '11px', maxHeight: '300px', overflow: 'auto' }}>
                {JSON.stringify({
                  android: result.fcm_response_android,
                  ios: result.fcm_response_ios
                }, null, 2)}
              </pre>
            </div>
          )}
        </div>
      )}
      
      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label htmlFor="title">Notification Title:</label>
          <input
            type="text"
            id="title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            required
            placeholder="Enter notification title"
          />
        </div>
        
        <div className="form-group">
          <label htmlFor="body">Notification Message:</label>
          <textarea
            id="body"
            value={body}
            onChange={(e) => setBody(e.target.value)}
            required
            placeholder="Enter notification message"
            rows={4}
          />
        </div>
        
        <div className="form-group">
          <label htmlFor="targetType">Send To:</label>
          <select
            id="targetType"
            value={targetType}
            onChange={(e) => setTargetType(e.target.value)}
          >
            <option value="all">All Users</option>
            <option value="team">Specific Team</option>
            <option value="match">Match Participants</option>
            <option value="players">Selected Players</option>
          </select>
        </div>
        
        {targetType === 'team' && (
          <div className="form-group">
            <label htmlFor="teamId">Select Team:</label>
            <select
              id="teamId"
              value={teamId}
              onChange={(e) => setTeamId(e.target.value)}
              required
            >
              <option value="">-- Select Team --</option>
              {teams.map((team) => (
                <option key={team.id} value={team.id}>
                  {team.name}
                </option>
              ))}
            </select>
          </div>
        )}
        
        {targetType === 'match' && (
          <div className="form-group">
            <label htmlFor="matchId">Select Match:</label>
            <select
              id="matchId"
              value={matchId}
              onChange={(e) => setMatchId(e.target.value)}
              required
            >
              <option value="">-- Select Match --</option>
              {matches.map((match) => (
                <option key={match.id} value={match.id}>
                  {new Date(match.match_date).toLocaleDateString()} - {match.location}
                </option>
              ))}
            </select>
          </div>
        )}
        
        {targetType === 'players' && (
          <div className="form-group">
            <label>Select Players:</label>
            <div className="player-selection">
              {players.map((player) => (
                <div key={player.id} className="player-checkbox">
                  <input
                    type="checkbox"
                    id={`player-${player.id}`}
                    value={player.id}
                    checked={selectedPlayers.includes(player.id)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedPlayers([...selectedPlayers, player.id]);
                      } else {
                        setSelectedPlayers(selectedPlayers.filter(id => id !== player.id));
                      }
                    }}
                  />
                  <label htmlFor={`player-${player.id}`}>
                    {player.first_name} {player.last_name}
                  </label>
                </div>
              ))}
            </div>
          </div>
        )}
        
        <button 
          type="submit" 
          className="submit-button"
          disabled={isLoading}
        >
          {isLoading ? 'Sending...' : 'Send Notification'}
        </button>
      </form>
    </div>
  );
};

export default PushNotificationAdmin; 